import {
  IconClipboardList,
  IconCoin,
  IconEyeExclamation,
  IconFileText,
  IconListDetails,
} from '@u21/tabler-icons';
import { FINTRAC_STR_ACTION_TAKEN_TAB_SECTION } from 'app/modules/fintracStr/formConfig/actionTaken';
import { FINTRAC_STR_DEFINITIONS_TAB_SECTION } from 'app/modules/fintracStr/formConfig/definitions';
import { FINTRAC_STR_DETAILS_OF_SUSPICION_TAB_SECTION } from 'app/modules/fintracStr/formConfig/detailsOfSuspicion';
import { FINTRAC_STR_RELATED_REPORTS_TAB_SECTION } from 'app/modules/fintracStr/formConfig/relatedReports';
import { FINTRAC_STR_REPORT_DETAILS_TAB_SECTION } from 'app/modules/fintracStr/formConfig/reportDetails';
import { FINTRAC_STR_TRANSACTIONS_TAB_SECTION } from 'app/modules/fintracStr/formConfig/transactions';

import {
  FintracFlattenedFormFieldConfigs,
  FintracForm,
  FintracStrFilingContent,
} from 'app/modules/fintracStr/models';
import { flattenFormConfig } from 'app/modules/fintracStr/transformers';
import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import { TableConfig } from 'app/shared/pagination/models';

export const FINTRAC_STR_TABS = {
  OVERVIEW: 'overview',
  REPORT_DETAILS: 'report-details',
  SUSPICIOUS_ACTIVITY_INFORMATION: 'suspicious-activity-information',
  TRANSACTIONS: 'transactions',
  RELATED_REPORTS: 'related-reports',
  ACTION_TAKEN: 'action-taken',
  DEFINITIONS: 'definitions',
} as const;

export const FINTRAC_STR_ANCHOR_URL_PARAMS: Readonly<string> = 'section';
export const FINTRAC_STR_DEFAULT_CONTENT: FintracStrFilingContent = {};

export const FINTRAC_STR_FORM_FIELDS: FintracForm[] = [
  {
    tabLabel: 'Report Details',
    tabKey: 'reportDetails',
    tabIcon: <IconListDetails />,
    tabPath: FINTRAC_STR_TABS.REPORT_DETAILS,
    tabSection: FINTRAC_STR_REPORT_DETAILS_TAB_SECTION,
  },
  {
    tabLabel: 'Suspicious Activity Information',
    tabKey: 'detailsOfSuspicion',
    tabIcon: <IconEyeExclamation />,
    tabPath: FINTRAC_STR_TABS.SUSPICIOUS_ACTIVITY_INFORMATION,
    tabSection: FINTRAC_STR_DETAILS_OF_SUSPICION_TAB_SECTION,
  },
  {
    tabLabel: 'Transactions',
    tabKey: 'transactions',
    tabIcon: <IconCoin />,
    tabPath: FINTRAC_STR_TABS.TRANSACTIONS,
    tabSection: FINTRAC_STR_TRANSACTIONS_TAB_SECTION,
  },
  {
    tabLabel: 'Subjects',
    tabKey: 'definitions',
    tabIcon: <IconFileText />,
    tabPath: FINTRAC_STR_TABS.DEFINITIONS,
    tabSection: FINTRAC_STR_DEFINITIONS_TAB_SECTION,
  },
  {
    tabLabel: 'Related Reports',
    tabKey: 'relatedReports',
    tabIcon: <IconClipboardList />,
    tabPath: FINTRAC_STR_TABS.RELATED_REPORTS,
    tabSection: FINTRAC_STR_RELATED_REPORTS_TAB_SECTION,
  },
  {
    tabLabel: 'Action Taken',
    tabKey: 'actionTaken',
    tabIcon: <IconFileText />,
    tabPath: FINTRAC_STR_TABS.ACTION_TAKEN,
    tabSection: FINTRAC_STR_ACTION_TAKEN_TAB_SECTION,
  },
];

// To optimise this by adding the computed config here once the form is finalised
export const FINTRAC_STR_FLATTENED_FORM_CONFIG: Readonly<
  Record<string, FintracFlattenedFormFieldConfigs>
> = Object.freeze(flattenFormConfig(FINTRAC_STR_FORM_FIELDS));

// Intuit XLSX file types for upload -> FINTRAC STR filings
export enum UploadXlsxFileType {
  PAYMENTS = 'PAYMENTS',
  PAYROLL = 'PAYROLL',
}

const UPLOAD_XLSX_FILE_TYPE_MAP: Record<UploadXlsxFileType, string> = {
  PAYMENTS: 'Payments',
  PAYROLL: 'Payroll',
};

export const UPLOAD_XLSX_FILE_TYPE_OPTIONS: U21SelectOptionProps<UploadXlsxFileType>[] =
  Object.entries(UPLOAD_XLSX_FILE_TYPE_MAP).map(
    ([key, label]: [UploadXlsxFileType, string]) => ({
      value: key,
      text: label,
    }),
  );

export const FINTRAC_STR_FORM_TAB_PATH_MAP: Readonly<Record<string, string>> =
  Object.freeze(
    FINTRAC_STR_FORM_FIELDS.reduce(
      (acc, curr) => {
        acc[curr.tabKey] = curr.tabPath;
        return acc;
      },
      {} as Record<string, string>,
    ),
  );

// Keep in sync with DEFAULT_FINTRAC_STR_TABLE_CONFIG in
// hogwarts/src/pensieve/models/default/default_table_configs.py.
export const DEFAULT_FINTRAC_STR_TABLE_CONFIG: TableConfig[] = [
  { key: 'id', type: 'text', label: 'ID' },
  { key: 'name', type: 'text', label: 'File name' },
  { key: 'status', type: 'label', label: 'Status' },
  { key: 'created_at', type: 'datetime', label: 'Created at' },
  { key: 'created_by', type: 'text', label: 'Created by', sortable: false },
  { key: 'reviewer', type: 'text', label: 'Reviewer', sortable: false },
  { key: 'deadline', type: 'text', label: 'Deadline', sortable: true },
  { key: 'queue', type: 'label', label: 'Queue' },
  { key: 'updated_at', type: 'datetime', label: 'Updated at', hidden: true },
  {
    key: 'first_alert_created_at',
    type: 'datetime',
    label: 'First alert created on',
    hidden: true,
  },
  { key: 'filed_at', type: 'datetime', label: 'Filed on', hidden: true },
  { key: 'report_type', type: 'label', label: 'Report type', hidden: true },
  {
    key: 'submitted',
    type: 'text',
    label: 'Submitted',
    hidden: true,
    sortable: false,
  },
];
